import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Tooltip, Typography } from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Popover, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom'
import { ceil } from 'lodash';

import Pagination from "../../../components/pagination/index";
import AgGrid from "../../../components/ag-grid-table/index";
import Modal from "../../../components/uiComponent/modal/index";

import InfoIcon from "../../../assets/icons/info-black.svg";

import { GetErroredItems } from "../../../slices/po";
import { CreateWorkOrder } from '../../../slices/work-orders';

import { PoDetailsWrapper } from "./style";

const defaultColDef = {
  resizable: true,
  sortable: false,
  filter: false,
  suppressRowClickSelection: true,
  suppressMenu: true,
  suppressMovable: true,
};

let gridApi;

const ErroredItemsModal = ({
  showErroredItemsModal,
  setShowErroredItemsModal,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { poId } = useParams();

  const {
    fetchingErroredItems,
    erroredItems,
    erroredItemsTotalCount,
  } = useSelector((state) => state.pos || {});

  const {
    creatingWorkOrder
  } = useSelector((state) => state.workOrders || {});

  const [page, setPage] = useState({ pageSize: 25, pageNumber: 1 });

  const InfoTooltipData = ({ products_distributions }) => {
    return (
      <PoDetailsWrapper className='mt-0'>
        <div className='container-box'>
          <div className='items'>
            <span style={{ fontWeight: 'bolder' }}>Identifier / Product</span>
            <span style={{ fontWeight: 'bolder' }}>WH Stock</span>
          </div>
          {
            products_distributions?.map(({
              name,
              bundle_quantity,
              identifier_value,
              warehouse_stock
            }) => {
              return (
                <div className='items'>
                  <span>{identifier_value} / {name || '---'}</span>
                  <span>{warehouse_stock || 0} - (Pack Of: {bundle_quantity || 0})</span>
                </div>
              )
            })
          }
        </div>
      </PoDetailsWrapper>
    )
  };

  const columnDefs = useMemo(() => [
    {
      headerName: "Title",
      sortable: true,
      filter: true,
      minWidth: 135,
      flex: 1,
      field: "item_name",
      cellRenderer: ({ data }) => {
        return (
          <Tooltip
            placement="bottom"
            title={data?.item_name || ''}
          >
            <span>{data?.item_name || '---'}</span>
          </Tooltip>
        )
      }
    },
    {
      headerName: "ASIN",
      field: "vendorasin.asin",
      minWidth: 120,
      flex: 1,
    },
    {
      headerName: "SKU",
      field: "sku",
      minWidth: 100,
      flex: 1,
    },
    {
      headerName: "Packed Quantity",
      minWidth: 100,
      flex: 1,
      field: "po_sku_mappings_v2.bundle_quantity",
      cellRenderer: ({ value }) => value || 0,
    },
    {
      headerName: "Available Stock",
      minWidth: 130,
      flex: 1,
      field: "total_quantity",
      cellRenderer: ({ value }) => value || 0,
    },
    {
      headerName: "WH Stock",
      minWidth: 130,
      flex: 1,
      field: "warehouse_stock",
      cellRenderer: ({ data, value }) => {
        const { products_distributions } = data || {};

        return (
          <div className='card-wrapper gap-1'>
            <span>{value || 0}</span>
            <Popover
              title={null}
              content={<InfoTooltipData products_distributions={products_distributions} />}
              placement='right'>
              <img src={InfoIcon} className='info-icon-de' alt="InfoIcon" title="Info-Icon" />
            </Popover>
          </div>
        )
      }
    },
    {
      headerName: "Gated Status",
      minWidth: 120,
      flex: 1,
      field: "sku_status",
      cellRenderer: ({ value }) => value || '---'
    }
  ], []);

  const handlePagination = (number, key) => {
    const value = Number(number);
    let newPage = {};

    if (key === 'pageSize') newPage = { pageNumber: 1, pageSize: value };
    else newPage = { ...page, pageNumber: value };

    setPage(newPage);
    dispatch(GetErroredItems({
      po_id: poId,
      page: newPage,
    }));
  };

  const refreshGridClick = () => {
    dispatch(GetErroredItems({
      po_id: poId,
      page,
    }))
  };

  const gridReady = (params) => {
    gridApi = params.api;
  };

  const { pageNumber, pageSize } = page || {};

  return (
    <>
      <Modal
        open={showErroredItemsModal}
        onClose={() => setShowErroredItemsModal(false)}
        onHide={() => setShowErroredItemsModal(false)}
        closeText="Cancel"
        saveText="Proceed without Errored Items"
        removeIcon
        className="table-ui-modal-wrapper"
        closeButton={true}
        onSave={() => {
          dispatch(CreateWorkOrder({
            po_id: poId
          }))
            .then(({ payload }) => {
              const { success, workOrderId } = payload || {};
              if (success) navigate(`/inventory/work-order/${workOrderId}`);
              setShowErroredItemsModal(false)
            });
        }}
      >
        <PoDetailsWrapper>
          <Typography fontSize={24} fontWeight={600} color="#1565D8" mb={2}>Errored Items</Typography>
          <Spin tip='Loading...' spinning={fetchingErroredItems || creatingWorkOrder}>
            <AgGrid
              user_referrals={erroredItems}
              defaultColDef={defaultColDef}
              columnDefinitions={columnDefs}
              gridReady={gridReady}
              rowHeight={64}
              headerHeight={36}
              height="365px"
              masterDetail={true}
              detailRowAutoHeight={true}
              className="sku-table"
              rowSelection='multiple'
            />
          </Spin>
          <Pagination
            total={erroredItemsTotalCount}
            totalPages={ceil(erroredItemsTotalCount / pageSize + 1)}
            pageNumber={pageNumber}
            pageLimit={pageSize}
            handleLimitChange={(e) => handlePagination(e, 'pageSize')}
            handlePageNumberChange={(e) => handlePagination(e, 'pageNumber')}
            handlePageChangeLeft={(e) => handlePagination(e, 'pageNumber')}
            handlePageChangeRight={(e) => handlePagination(e, 'pageNumber')}
            refreshGridClick={refreshGridClick}
          />
        </PoDetailsWrapper>
      </Modal>
    </>
  )
}

export default ErroredItemsModal